import { createServerFn } from "@tanstack/react-start";

export type MortgageRateSnapshot = {
  seriesId: string;
  seriesLabel: string;
  latest: { date: string; value: number };
  previous: { date: string; value: number } | null;
  fourWeeksAgo: { date: string; value: number } | null;
  yearAgo: { date: string; value: number } | null;
  weeklyChange: number | null;
  monthlyChange: number | null;
  yearlyChange: number | null;
  history: { date: string; value: number }[]; // most recent 12 obs
  source: string;
  fetchedAt: string;
};

const SERIES: Record<string, string> = {
  MORTGAGE30US: "30-Year Fixed Rate Mortgage Average (Freddie Mac PMMS)",
  MORTGAGE15US: "15-Year Fixed Rate Mortgage Average (Freddie Mac PMMS)",
  SOFR: "Secured Overnight Financing Rate (NY Fed)",
  DGS10: "10-Year Treasury Constant Maturity Rate",
};

async function fetchSeries(seriesId: string): Promise<MortgageRateSnapshot> {
  const url = `https://fred.stlouisfed.org/graph/fredgraph.csv?id=${seriesId}`;
  let res: Response | null = null;
  let lastErr = "";
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      res = await fetch(url, { headers: { accept: "text/csv" }, signal: AbortSignal.timeout(8_000) });
      if (res.ok) break;
      lastErr = `responded ${res.status}`;
    } catch (e) {
      lastErr = (e as Error).message;
    }
    res = null;
    if (attempt === 0) await new Promise((r) => setTimeout(r, 750));
  }
  if (!res) throw new Error(`FRED ${seriesId} ${lastErr} (${url})`);
  const csv = await res.text();
  const rows = csv
    .trim()
    .split(/\r?\n/)
    .slice(1)
    .map((line) => {
      const [date, raw] = line.split(",");
      const value = parseFloat(raw);
      return { date, value };
    })
    .filter((r) => Number.isFinite(r.value));

  if (!rows.length) throw new Error(`FRED ${seriesId} returned no observations`);

  const latest = rows[rows.length - 1];
  const previous = rows[rows.length - 2] ?? null;
  const fourWeeksAgo = rows[rows.length - 5] ?? null;
  const yearAgo = rows[rows.length - 53] ?? null;

  const round = (n: number) => Math.round(n * 100) / 100;
  return {
    seriesId,
    seriesLabel: SERIES[seriesId] ?? seriesId,
    latest,
    previous,
    fourWeeksAgo,
    yearAgo,
    weeklyChange: previous ? round(latest.value - previous.value) : null,
    monthlyChange: fourWeeksAgo ? round(latest.value - fourWeeksAgo.value) : null,
    yearlyChange: yearAgo ? round(latest.value - yearAgo.value) : null,
    history: rows.slice(-12),
    source: "Federal Reserve Economic Data (FRED) · Freddie Mac PMMS",
    fetchedAt: new Date().toISOString(),
  };
}

export const getMortgageMarketSnapshot = createServerFn({ method: "GET" }).handler(async () => {
  const [thirty, fifteen] = await Promise.all([
    fetchSeries("MORTGAGE30US"),
    fetchSeries("MORTGAGE15US").catch(() => null),
  ]);
  return { thirtyYear: thirty, fifteenYear: fifteen };
});

export type BenchmarkRate = {
  seriesId: string;
  label: string;
  value: number;
  /** FRED observation date for `value`. */
  date: string;
  changeFromPrior: number | null;
  /** When this observation was last successfully confirmed with FRED. */
  verifiedAt: string;
  /** True when FRED could not be reached just now and this is the last confirmed observation. */
  stale: boolean;
};

export type BenchmarkResponse = {
  benchmarks: BenchmarkRate[];
  /** Series we could not show at all (never fetched successfully). */
  unavailable: string[];
  source: string;
};

/** Official FRED series behind each displayed benchmark. */
export const BENCHMARK_SERIES = [
  { seriesId: "MORTGAGE30US", label: "30-Yr Fixed (Freddie Mac PMMS)" },
  { seriesId: "SOFR", label: "SOFR (overnight)" },
  { seriesId: "DGS10", label: "10-Yr Treasury (DGS10)" },
] as const;

// Last successfully confirmed observation per series (per server instance).
const lastGood = new Map<string, BenchmarkRate>();
let memo: { at: number; ttl: number; value: BenchmarkResponse } | null = null;
const MEMO_MS = 15 * 60_000;
const FAILURE_COOLDOWN_MS = 2 * 60_000;

/** Persisted last-confirmed observation, shared by every server instance. */
const CACHE_KEY = (id: string) => `fred:benchmark:${id}`;
async function persistGood(rate: BenchmarkRate) {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("ai_response_cache").delete().eq("cache_key", CACHE_KEY(rate.seriesId));
    await supabaseAdmin.from("ai_response_cache").insert({
      cache_key: CACHE_KEY(rate.seriesId),
      model: "fred",
      feature: "benchmark",
      response: JSON.stringify(rate),
      expires_at: new Date(Date.now() + 60 * 24 * 3600_000).toISOString(),
    } as never);
  } catch (e) {
    console.error("FRED persist failed:", (e as Error).message);
  }
}
async function readPersisted(seriesId: string): Promise<BenchmarkRate | null> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("ai_response_cache")
      .select("response")
      .eq("cache_key", CACHE_KEY(seriesId))
      .maybeSingle();
    return data ? (JSON.parse((data as { response: string }).response) as BenchmarkRate) : null;
  } catch {
    return null;
  }
}

async function fetchBenchmark(seriesId: string, label: string): Promise<BenchmarkRate | null> {
  try {
    const s = await fetchSeries(seriesId);
    const rate: BenchmarkRate = {
      seriesId,
      label,
      value: s.latest.value,
      date: s.latest.date,
      changeFromPrior: s.previous ? Math.round((s.latest.value - s.previous.value) * 100) / 100 : null,
      verifiedAt: new Date().toISOString(),
      stale: false,
    };
    const prevDate = lastGood.get(seriesId)?.date;
    lastGood.set(seriesId, rate);
    // Write only when this instance hasn't persisted this observation yet.
    if (prevDate !== rate.date) await persistGood(rate);
    return rate;
  } catch (e) {
    console.error(`FRED ${seriesId} failed:`, (e as Error).message);
    const prev = lastGood.get(seriesId) ?? (await readPersisted(seriesId));
    return prev ? { ...prev, stale: true } : null;
  }
}

export const getBenchmarkRates = createServerFn({ method: "GET" }).handler(async (): Promise<BenchmarkResponse> => {
  if (memo && Date.now() - memo.at < memo.ttl) return memo.value;
  const results = await Promise.all(BENCHMARK_SERIES.map((b) => fetchBenchmark(b.seriesId, b.label)));
  const value: BenchmarkResponse = {
    benchmarks: results.filter((x): x is BenchmarkRate => x !== null),
    unavailable: BENCHMARK_SERIES.filter((_, i) => !results[i]).map((b) => b.label),
    source: "Federal Reserve Economic Data (FRED) · Freddie Mac · NY Fed · U.S. Treasury",
  };
  // Fresh answers are reused for 15 min; a failure is retried after a short cooldown
  // (never on every page view) so a FRED outage doesn't turn into request storms.
  const allFresh = value.benchmarks.length === BENCHMARK_SERIES.length && value.benchmarks.every((b) => !b.stale);
  memo = { at: Date.now(), ttl: allFresh ? MEMO_MS : FAILURE_COOLDOWN_MS, value };
  return value;
});
